import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { fmtEur } from "@/lib/quote";

const CATEGORIES = ["Bar", "Food", "Sala", "Intrattenimento", "Extra"];
const EMPTY = { name: "", category: "Bar", menuGroup: "", price: 0, vat: 22, unit: "persona", discount: 0, active: true, description: "" };

export const CatalogManager = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try { setProducts((await api.get("/admin/catalog")).data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return toast.error("Inserisci il nome del prodotto");
    try {
      if (editing) await api.put(`/admin/catalog/${editing}`, form);
      else await api.post("/admin/catalog", form);
      toast.success("Listino aggiornato");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (p) => {
    await api.put(`/admin/catalog/${p.id}`, { ...p, active: !p.active });
    load();
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/admin/catalog/${p.id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const inp = "bg-[#1E293B] border-slate-700 text-white h-9";

  return (
    <div className="space-y-6">
      <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-3">{editing ? "Modifica prodotto" : "Nuovo prodotto"}</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input data-testid="product-name-input" className={inp} placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="product-category-select" className={`${inp} rounded-md border px-2 text-sm`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Input data-testid="product-group-input" className={inp} placeholder="Menu group" value={form.menuGroup} onChange={(e) => setForm({ ...form, menuGroup: e.target.value })} />
          <Input data-testid="product-price-input" className={inp} type="number" step="0.5" placeholder="Prezzo €" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} />
          <select data-testid="product-vat-select" className={`${inp} rounded-md border px-2 text-sm`} value={form.vat} onChange={(e) => setForm({ ...form, vat: Number(e.target.value) })}>
            <option value={10}>IVA 10%</option><option value={22}>IVA 22%</option>
          </select>
          <select data-testid="product-unit-select" className={`${inp} rounded-md border px-2 text-sm`} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            <option value="persona">A persona</option><option value="fisso">Prezzo fisso</option>
          </select>
          <Input data-testid="product-description-input" className={`${inp} sm:col-span-3`} placeholder="Descrizione (visibile al cliente nel wizard)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="flex gap-2 mt-3">
          <Button data-testid="product-save-button" className="bg-[#C85A32] hover:bg-[#a94a28]" onClick={save}><Plus className="w-4 h-4 mr-1" /> {editing ? "Salva" : "Aggiungi"}</Button>
          {editing && <Button variant="ghost" className="text-slate-400" onClick={() => { setEditing(null); setForm(EMPTY); }}>Annulla</Button>}
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const items = products.filter((p) => p.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-2">{cat} ({items.length})</h3>
            <div className="space-y-1.5">
              {items.map((p) => (
                <div key={p.id} data-testid={`catalog-row-${p.id}`} className={`bg-[#1E293B] border border-slate-700 rounded-lg px-3 py-2 flex items-center gap-3 ${!p.active ? "opacity-50" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{p.name}</span>
                    <span className="text-xs text-slate-500 ml-2">{p.menuGroup} · IVA {p.vat}% · {p.unit === "persona" ? "a persona" : "fisso"}</span>
                  </div>
                  <span className="font-mono-num text-sm font-bold text-[#F0B429]">{fmtEur(p.price)}</span>
                  <Switch data-testid={`product-active-${p.id}`} checked={p.active} onCheckedChange={() => toggleActive(p)} />
                  <button data-testid={`product-edit-${p.id}`} onClick={() => { setEditing(p.id); setForm({ name: p.name, category: p.category, menuGroup: p.menuGroup, price: p.price, vat: p.vat, unit: p.unit, discount: p.discount || 0, active: p.active, description: p.description || "" }); window.scrollTo(0, 0); }} className="text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`product-delete-${p.id}`} onClick={() => del(p)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
